import React from "react";

/**
 * Nodes handed to a client component from a server component — the `button`
 * slots and the `title:` fragments our page files build in `data.tsx` — arrive
 * still flagged by the Flight serializer as "this was in a list and has no key"
 * (`_store.validated === 2`). Rendering one inside a children array then logs
 * the dev-only "Each child in a list should have a unique key prop" warning,
 * even where the array is a static JSX position.
 *
 * `React.Children.toArray` does not clear it: it clones through
 * `cloneAndReplaceKey`, which copies the flag onto the clone. `cloneElement`
 * builds a fresh element, so the flag goes. A server-rendered client component
 * arrives as a lazy wrapper rather than an element and cannot be cloned at all,
 * so it gets a keyed Fragment instead — which also moves it out of the array
 * position React is complaining about. Rendered output is unchanged either way.
 */
function keyed(child: React.ReactNode, key: string): React.ReactNode {
  if (React.isValidElement(child)) {
    return React.cloneElement(child, { key });
  }

  // Lazy wrappers (client components sent over the RSC boundary) and portals.
  if (child !== null && typeof child === "object") {
    return <React.Fragment key={key}>{child}</React.Fragment>;
  }

  // Strings, numbers, null and undefined never need a key.
  return child;
}

export function keyedNodes(node: React.ReactNode): React.ReactNode {
  if (Array.isArray(node)) {
    return node.map((child, index) => keyed(child, `node-${index}`));
  }

  return keyed(node, "node-0");
}
